"use client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import "./globals.css";
import { useEffect, useState, useRef, Suspense } from "react";
import { Toaster } from "react-hot-toast";
import Lenis from "lenis";
import { CollectionProvider } from "@/context/Collections";
import { ActivitiesProvider } from "@/context/Activities";
import { TrainingProvider } from "@/context/Training";
import { ExpeditionProvider } from "@/context/Expeditions";
import { BlogProvider } from "@/context/Blog";
import { usePathname } from "next/navigation";
import Preloader from "./preloader/Preloader";
import HomeMain from "@/components/website/Homepage/HomeMain";
import WhatsApp from "@/components/website/Homepage/whatsapp";
import GoogleTranslateScript from "@/components/GoogleTranslateScript";

export default function ClientLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const currentRoute = usePathname();
  const [showPreloader, setShowPreloader] = useState(() => {
    if (typeof window !== "undefined") {
      if (window.location.pathname !== "/") return false;
      const hasSeen = sessionStorage.getItem("preloaderShown");
      return !hasSeen;
    }
    return currentRoute === "/";
  });
  const [isMounted, setIsMounted] = useState(false);
  const lenisRef = useRef<Lenis | null>(null);

  // Sync state if user navigates to subpages
  useEffect(() => {
    setIsMounted(true);
    if (currentRoute !== "/") {
      setShowPreloader(false);
    }
  }, [currentRoute]);

  const handlePreloaderComplete = () => {
    sessionStorage.setItem("preloaderShown", "true");
    setShowPreloader(false);
    if (lenisRef.current) {
      lenisRef.current.resize();
    }
  };

  useEffect(() => {
    // Initialize Lenis smooth scrolling with autoResize
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      autoResize: true,
    });
    lenisRef.current = lenis;

    // Animation frame for smooth scrolling
    let animationFrameId: number;
    const raf = (time: number) => {
      lenis.raf(time);
      animationFrameId = requestAnimationFrame(raf);
    };
    animationFrameId = requestAnimationFrame(raf);

    // Keep Lenis scroll limit in sync as images and dynamic content load
    const resizeObserver = new ResizeObserver(() => {
      lenis.resize();
    });
    if (document.body) {
      resizeObserver.observe(document.body);
    }

    return () => {
      resizeObserver.disconnect();
      cancelAnimationFrame(animationFrameId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  return (
    <>
      <Suspense fallback={null}>
        <GoogleTranslateScript />
      </Suspense>
      <Toaster />
      <style jsx global>{`
        body {
          overflow: ${showPreloader ? "hidden" : "auto"};
        }
      `}</style>

      {/* Preloader overlay on first visit to homepage */}
      {showPreloader && (
        <Preloader onComplete={handlePreloaderComplete} />
      )}

      {/* Main app tree - renders underneath so hero & assets are ready when preloader fades out */}
      <div>
        <CollectionProvider>
          <ActivitiesProvider>
            <TrainingProvider>
              <ExpeditionProvider>
                <BlogProvider>
                  <Navbar />
                  <div className="w-full h-full">
                    {currentRoute === "/" ? <HomeMain /> : children}
                  </div>
                  <Footer />
                  <WhatsApp />
                </BlogProvider>
              </ExpeditionProvider>
            </TrainingProvider>
          </ActivitiesProvider>
        </CollectionProvider>
      </div>
    </>
  );
}
